<template>
    <Modal class="mellon-webecr-redeem-modal"
           data-component="MellonWebECRRedeemModal"
           :cancel="cancel"
           icon="fa-solid fa-tablet"
           :ok="redeem"
           :ok-enabled="okEnabled"
           ok-label="MELLON_WEBECR_REDEEM_MODAL.OK__BTN"
           :ok-spinner="redeeming"
           title="MELLON_WEBECR_REDEEM_MODAL.MODAL_TITLE">
        <!-- Error message (if applicable) -->
        <ErrorMessage v-if="error"
                      :error="error" />

        <form class="form-h"
              @submit.prevent="redeem"
              @keydown.enter="redeem">
            <div class="form-group">
                <label for="mellon-webecr-redeem-code"
                       class="control-label">
                    {{ l('MELLON_WEBECR_REDEEM_MODAL.CODE__LABEL') }}
                </label>
                <div class="form-h__right">
                    <input id="mellon-webecr-redeem-code"
                           v-model="formData.code"
                           type="text"
                           class="form-control input-lg"
                           minlength="1"
                           maxlength="200">
                    <p class="help-block">
                        {{ l('MELLON_WEBECR_REDEEM_MODAL.CODE__DESC') }}
                    </p>
                </div>
            </div>

            <div class="form-group">
                <label for="mellon-webecr-redeem-label"
                       class="control-label">
                    {{ l('MELLON_WEBECR_REDEEM_MODAL.LABEL__LABEL') }}
                </label>
                <div class="form-h__right">
                    <input id="mellon-webecr-redeem-label"
                           v-model="formData.label"
                           type="text"
                           class="form-control input-lg"
                           minlength="1"
                           maxlength="200">
                    <p class="help-block">
                        {{ l('MELLON_WEBECR_REDEEM_MODAL.LABEL__DESC') }}
                    </p>
                </div>
            </div>
        </form>
    </Modal>
</template>

<script>
/**
 * This modal allows a user to register a new Mellon WebECR terminal.
 * This will give us an API to make requests for this specific terminal.
 *
 * In order for the client software to retrieve the X-Api-Key, the following workflow is to be followed:
 * 1. The user turns on the EFT-POS or opens the softPOS application on their phone
 * 2. The user navigates to the relevant POS menu option named "Link with 3rd party services"
 * 3. The EFT-POS shows an authentication code. The user enters it in the client software.
 * 4. The client software exchanges (redeems) the authentication code with the API key, using the
 *    authentication code redeem API call as described below
 * 5. The client software saves the API key and uses it for the rest of the integration lifetime
 *
 * The client software can save/cache the API key and use it to start transactions at any point in time. The
 * merchant has the ability to revoke the access permissions at any time, so the Api key may become invalid. In
 * this case, the client software should handle this as an error condition.
 */
import ErrorMessage from '@/vue/components/Errors/ErrorMessage';
import Modal from '@/vue/components/Modals/Modal';
import { container } from '@/container';
import { mapState } from 'vuex';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

/**
 * @typedef {Object} MellonWebECRRedeemModalResult
 * @property {MellonWebECRRedeemAuthCodeResponse} response
 * @property {string} label
 * @property {Date} redeemedAt
 */

export default {
    components: {
        ErrorMessage,
        Modal,
    },

    mixins: [
        ModalPropsMixin,
    ],

    data() {
        return {
            /**
             * @type {?Error}
             */
            error: null,

            formData: {
                code: null,
                label: null,
            },

            redeeming: false,
        };
    },

    computed: {
        ...mapState(['isDev', 'isStaging']),

        okEnabled() {
            return !this.redeeming
                && !!(this.formData.code && this.formData.label);
        },
    },

    methods: {
        async redeem() {
            if (!this.okEnabled) {
                return; // Why are you redeeming?!
            }

            this.error = null;
            this.redeeming = true;

            // Send the authentication code to Mellon to redeem it and get an API key.
            try {
                /**
                 * @type {MellonWebECRRedeemAuthCodeRequest}
                 */
                const redeemRequest = {
                    // The type of authorization code. Always set to "webecr".
                    Type: 'webecr',
                    // The authorization code as shown by the POS and entered by the user.
                    Code: this.formData.code,
                };

                let response = {};

                if ((this.isDev || this.isStaging) && this.formData.code === '123') {
                    // In dev mode return a known virtual test terminal to simulate redeeming.
                    response = {
                        Id: 'cGBn2ByXT9SIA_pFg4KjzQ',
                        Type: 'webecr',
                    };
                } else if ((this.isDev || this.isStaging) && this.formData.code === '0118999') {
                    // In dev mode return a known physical test terminal (somewhere) to simulate redeeming.
                    response = {
                        Id: 'VBA-uub1SPivgt5i27JjHQ',
                        Type: 'webecr',
                    };
                } else {
                    // Call the real API.
                    response = await container.mellonWebECRDriver.client.redeemAuthCode(redeemRequest);
                }

                if (response?.Id) {
                    /**
                     * @type {MellonWebECRRedeemModalResult}
                     */
                    const result = {
                        response,
                        label: this.formData.label,
                        redeemedAt: new Date(),
                    };
                    this.resolve(result);
                    return;
                }

                this.error = new Error(`Unexpected response from Mellon WebECR: ${JSON.stringify(response)}`);
            } catch (error) {
                this.error = error;
            } finally {
                this.redeeming = false;
            }
        },
    },
};
</script>
